import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../../store/AuthContext'
import { useToast } from '../../components/Toast'
import scoresService from '../../services/scoresService'
import employeesService from '../../services/employeesService'
import { RankingTable } from '../../components/RankingTable'
import { getQuarterFromMonth } from '../../utils/formatters'

export const RankingPage = () => {
  const { user } = useAuth()
  const toast = useToast()
  const isAdmin = user?.role === 'admin'

  // Current date
  const now = new Date()
  const currentYear = now.getFullYear()
  const currentQuarter = getQuarterFromMonth(now.getMonth() + 1)

  // State for filters
  const [selectedYear, setSelectedYear] = useState(currentYear)
  const [selectedQuarter, setSelectedQuarter] = useState(currentQuarter)
  const [exporting, setExporting] = useState(false)

  // Generate year options (current year and 2 previous)
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2]

  // Fetch current user's employee data
  const { data: employees = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: () => employeesService.getAll(),
  })

  const myEmployee = employees.find((e) => e.user_detail.id === user?.id)

  // Fetch quarterly summary
  const { data: quarterlySummary, isLoading } = useQuery({
    queryKey: ['quarterly-summary', selectedYear, selectedQuarter],
    queryFn: () => scoresService.getQuarterlySummary(selectedYear, selectedQuarter),
  })

  // Calculate my position
  const sortedEmployees = quarterlySummary?.employees
    ? [...quarterlySummary.employees].sort((a, b) => b.average - a.average)
    : []
  const myPosition = myEmployee
    ? sortedEmployees.findIndex((e) => e.employee_id === myEmployee.id) + 1
    : null

  const myData = quarterlySummary?.employees.find(
    (e) => e.employee_id === myEmployee?.id
  )

  const handleExport = async () => {
    setExporting(true)
    try {
      await scoresService.exportScores(selectedYear)
      toast.success(`Planilha de ${selectedYear} exportada com sucesso.`)
    } catch {
      toast.error('Erro ao exportar pontuação.')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Ranking
        </h1>
        {isAdmin && (
          <button
            onClick={handleExport}
            disabled={exporting}
            className="px-4 py-2 text-sm font-medium bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-btn text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 flex items-center gap-2 shadow"
          >
            {exporting ? (
              <>
                <span className="animate-spin inline-block w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full" />
                Exportando...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Exportar {selectedYear}
              </>
            )}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Year Select */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Ano
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          {/* Quarter Select */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Trimestre
            </label>
            <select
              value={selectedQuarter}
              onChange={(e) => setSelectedQuarter(Number(e.target.value) as 1 | 2 | 3 | 4)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value={1}>Q1 - Janeiro a Março</option>
              <option value={2}>Q2 - Abril a Junho</option>
              <option value={3}>Q3 - Julho a Setembro</option>
              <option value={4}>Q4 - Outubro a Dezembro</option>
            </select>
          </div>
        </div>
      </div>

      {/* My Position Summary */}
      {myEmployee && myData && myPosition && myData.average !== 0 && (
        <div className="bg-primary/10 border border-primary/20 rounded-card p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                Sua Posição
              </p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {myPosition}º lugar
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Média: {myData.average > 0 ? '+' : ''}{myData.average} pts • Total: {myData.total_accumulated > 0 ? '+' : ''}{myData.total_accumulated} pts
              </p>
            </div>
            <div className="text-5xl">
              {myPosition === 1 ? '🥇' : myPosition === 2 ? '🥈' : myPosition === 3 ? '🥉' : '🏆'}
            </div>
          </div>
        </div>
      )}

      {/* Stats Summary */}
      {quarterlySummary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              Total de Funcionários
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {quarterlySummary.employees.length}
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              Maior Pontuação
            </p>
            <p className="text-2xl font-bold text-success">
              {sortedEmployees.length > 0
                ? `${sortedEmployees[0].average > 0 ? '+' : ''}${sortedEmployees[0].average}`
                : '-'}
            </p>
            {sortedEmployees.length > 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                {sortedEmployees[0].employee_name}
              </p>
            )}
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              Média Geral
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {quarterlySummary.employees.length > 0
                ? Math.round(
                    quarterlySummary.employees.reduce((sum, e) => sum + e.average, 0) /
                      quarterlySummary.employees.length
                  )
                : 0}
            </p>
          </div>
        </div>
      )}

      {/* Ranking Table */}
      <RankingTable
        employees={quarterlySummary?.employees ?? []}
        isLoading={isLoading}
        highlightEmployeeId={myEmployee?.id}
      />
    </div>
  )
}

export default RankingPage
